import {
    Component,
    OnInit,
    Input,
    Output,
    EventEmitter,
    OnDestroy,
    ChangeDetectorRef,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, tap, takeUntil } from 'rxjs/operators';
import { UIService } from '@intranet/core/services/ui.service';
import { ClientsService } from '@intranet/modules/clients/clients.service';
import { LoginService } from '@intranet/modules/login/login.service';
import { FinanceService } from '../../finance.service';
import {
    canLoggedInUserActionNextStep,
    convertFinanceTypeToName,
    navigateToConvert,
    parseFinanceNumber,
} from '../../finance.helpers';
import { FinanceStatus, Finance, Currency, FinanceSearchQuery } from '../../finance.types';
import { lastValueFrom, Subject } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { MatMenuTrigger } from '@angular/material/menu';
import { DateAdapter } from '@angular/material/core';
import { ActionService } from '@intranet/modules/actions/action.service';
import { ActionStatus, JobAction, JobActionType, JobActionUpdate } from '@intranet/modules/actions/action.types';
import { getStatusByValue, shouldHighlightDate } from '@intranet/modules/actions/action.utils';

@Component({
    selector: 'finance-listing-table',
    templateUrl: './finance-listing-table.component.html',
    styleUrls: ['./finance-listing-table.component.scss'],
})
export class FinanceListingTableComponent implements OnInit, OnDestroy {
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @Input() financeType;
    @Input() status: FinanceStatus | 'ALL';
    @Input() canCreate: boolean = false;
    @Input() canDelete: boolean = false;
    @Input() sortBy: string;
    @Input() sortDir: string;
    @Input() pageIndex = 0;
    @Input() totalResults = 0;
    @Input() showMarginBar: boolean = false;
    @Input() showActionColumns: boolean = false;
    @Output() copy = new EventEmitter<Finance>();
    @Output() sort = new EventEmitter<string>();
    @Output() nextStatusStep = new EventEmitter<Finance>();
    @Output() delete = new EventEmitter<Finance>();
    @Output() paginate = new EventEmitter<PageEvent>();
    @Output() actionUpdate = new EventEmitter<JobAction>();
    @Output() actionSelected = new EventEmitter<JobActionUpdate>();
    @ViewChild('bulkMenuTrigger', { read: MatMenuTrigger, static: false })
    bulkMenuTrigger!: MatMenuTrigger;
    currencies: Currency[];
    finances: Finance[];
    // Job-level margin data (YTD ring + total ring), keyed by jobId.
    // Only populated when showMarginBar is true (the Month End tab).
    marginData: { [jobId: number]: any } = {};
    // Invoice-type actions for the jobs behind the current page, keyed by
    // jobId, ordered exactly as the backend returns them (DeadlineDate, then
    // ScheduleDate, then JobActionId). Index 0 renders on the finance row
    // itself; any further entries render as extra rows underneath.
    // Only populated when showActionColumns is true (the Month End tab).
    actionsData: { [jobId: number]: JobAction[] } = {};
    GBP_CURRENCY_ID = 1;
    EUR_CURRENCY_ID = 2;
    @Output() selectionChanged = new EventEmitter<Finance[]>();
    public selectedFinances: Finance[] = [];
    private _currentFilterQuery: FinanceSearchQuery;

    constructor(
        private _route: ActivatedRoute,
        private _financeService: FinanceService,
        private _clientService: ClientsService,
        private _uiService: UIService,
        private _loginService: LoginService,
        private _router: Router,
        private cd: ChangeDetectorRef,
        private _actionService: ActionService,
        private dateAdapter: DateAdapter<any>,
    ) {}

    ngOnInit(): void {
        // keep track of whatever the parent last asked for
        this._financeService.filterQuery$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(q => this._currentFilterQuery = q);

        this.loadFinances();
    }

    loadFinances(): void {
        this._financeService.finances$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (response) => {
                if (!response.length) {
                    this.finances = response;
                } else {
                  // If the finances are loaded, we need to fetch the next status for each finance
                    await Promise.all(response.map(async (f) => {
                        f.nextStatus = await lastValueFrom(
                            this._financeService.getNextStatus(
                                f.version.financeStatusCode!,
                                f.financeType!
                            )
                        );
                    }));

                    this.finances = response;
                }

                this.loadMarginData(response);
                this.loadActionsData(response);

                // If the user has selected finances, filter the list to only include those
                this.selectedFinances = this.selectedFinances
                    .map(sel =>
                        this.finances.find(f => f.version.id === sel.version.id)
                    )
                    .filter((f): f is Finance => !!f);

                // If the user has selected finances, emit the selection
                this.selectionChanged.emit(this.selectedFinances);
            });
    }

    getFinanceNumber(financeNo, financeType, versionNumber) {
        return parseFinanceNumber(financeNo, financeType, versionNumber);
    }

    getNextStatusLabel(finance: Finance) {
        const s = finance.nextStatus;
        if (s) {
            if (s.financeStatusCode === 'CLTAPL') {
                return 'Send to client';
            }
            return s.title;
        }

        return finance.financeType === 1
            ? 'Convert to Invoice'
            : 'Convert to Credit Note';
    }


    findCurrency(currencyId: number) {
        return this._financeService.currencies$.pipe(
            map(
                (currencies) =>
                    currencies.find((c) => c.currencyId === currencyId)?.code
            )
        );
    }

    // Display-only variant of findCurrency() for the Currency column header
    // (£/€). The Currency table's "Symbol" field actually holds the
    // ISO 3-letter code (e.g. "GBP"/"EUR"), not a single-character symbol, so
    // this maps that code to the real character instead of trusting the field
    // name. This project only ever deals in Sterling or Euros.
    // findCurrency()'s code is still used as-is for the Value column's
    // currency pipe, which requires an ISO 4217 code, not a symbol.
    findCurrencySymbol(currencyId: number) {
        return this._financeService.currencies$.pipe(
            map((currencies) => {
                const code = currencies.find((c) => c.currencyId === currencyId)?.code;
                if (code === 'GBP') return '£';
                if (code === 'EUR') return '€';
                return code;
            })
        );
    }

    findClientName(clientId: number) {
        return this._clientService.clients$.pipe(
            map((clients) => {
                let client = clients.find((c) => c.clientId === clientId);
                if (client) {
                    return client.displayName || client.clientName;
                }

                return 'Unknown client';
            })
        );
    }

    financeStatusLabel(financeStatusCode: string, isRejected: boolean) {
        return this._financeService.financeStatuses$.pipe(
            map((x) =>
                x.find((s) => s.financeStatusCode === financeStatusCode)
            ),
            map((code) => (isRejected ? 'Rejected' : code.titleCurrentStatus))
        );
    }

    selectFinance(financeVersionId: number): void {
        this._router.navigate([
            `/finance/${convertFinanceTypeToName(
                this.financeType,
                true
            )}/${financeVersionId}`,
        ]);
    }

    getValueTotal(currencyId: number): number {
        return this.finances
        .filter((f) => f.version.currencyId === currencyId)
        .reduce((total, finance) => {
            if (finance.version && finance.version.subTotal) {
                return total + finance.version.subTotal;
            }
            return total;
        }, 0);
    }

    getMarginTotal(currencyId: number): number {
        return this.finances
        .filter((f) => f.version.currencyId === currencyId)
        .reduce((total, finance) => {
            if (finance.version && finance.version.margin) {
                return total + finance.version.margin;
            }
            return total;
        }, 0);
    }



    isOwnerAndNoApprovals(finance: Finance): boolean {
        if (
            finance.user.id === this._loginService.userId &&
            !finance.version.hasApprovals
        ) {
            return true;
        }

        return false;
    }

    canActionNextStep(finance: Finance) {
        let isFinance = this._loginService.checkAuth(['finance']);
        let isAdmin = this._loginService.checkAuth(['admin']);
        return canLoggedInUserActionNextStep(
            finance,
            finance.nextStatus,
            isFinance,
            isAdmin
        );
    }

    canCreateCreditNote(finance: Finance) {
        return finance.financeId &&
            !finance.version.isRejected &&
            !finance.nextStatus &&
            finance.financeType === 2 &&
                this._loginService.checkAuth([
                    'finance'
                ]);
    }

    onPaginate(e: PageEvent) {
        this.paginate.emit(e);
    }

    onOpen(finance: Finance): void {
        this.selectFinance(finance.version.id);
    }

    public financeHref(fin: Finance): string {
        const tree = this._router.createUrlTree([
            '/finance',
            convertFinanceTypeToName(this.financeType, true),
            fin.version.id
        ]);
        return this._router.serializeUrl(tree);
    }

    onViewPdf(finance: Finance): void {
        window.open(`/finance/downloads/${finance.version.id}/view`, '_blank');
    }

    onDownloadPdf(finance: Finance): void {
        window.open(
            `/finance/downloads/${finance.version.id}/download`,
            '_blank'
        );
    }

    onCopy(finance: Finance): void {
        this.copy.emit(finance);
    }

    onCreateCreditNote(financeType: number, versionId: number) {
        navigateToConvert(versionId, financeType, this._router);
    }

    onNextStatusStep(finance: Finance): void {
        this.nextStatusStep.emit(finance);
    }

    onSort(prop: string) {
        this.sort.emit(prop);
    }

    // Bulk-fetches job-level margin data for the jobs behind the current
    // page of finance rows. Only called when showMarginBar is true, so
    // Invoices/Estimates never pay for this extra request.
    private loadMarginData(finances: Finance[]): void {
        if (!this.showMarginBar) {
            return;
        }

        const jobIds = Array.from(new Set(
            finances.filter(f => f.jobId).map(f => f.jobId)
        ));

        if (!jobIds.length) {
            this.marginData = {};
            return;
        }

        this._financeService.getMarginDataForJobs(jobIds)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(data => {
                const byJobId: { [jobId: number]: any } = {};
                data.forEach(jv => byJobId[jv.jobId] = jv);
                this.marginData = byJobId;
            });
    }

    onGoToJob(finance: Finance): void {
        this._router.navigate(['/jobs/detail', finance.jobId]);
    }

    // Bulk-fetches the Invoice-type actions for the jobs behind the current
    // page of finance rows. Only called when showActionColumns is true
    // (Month End). The backend endpoint already sorts by DeadlineDate, then
    // ScheduleDate, then JobActionId, so partitioning the flat result by
    // jobId preserves that order within each job's bucket - no client-side
    // re-sort needed.
    private loadActionsData(finances: Finance[]): void {
        if (!this.showActionColumns) {
            return;
        }

        const jobIds = Array.from(new Set(
            finances.filter(f => f.jobId).map(f => f.jobId)
        ));

        if (!jobIds.length) {
            this.actionsData = {};
            return;
        }

        this._actionService.getActionsForJobs(jobIds, JobActionType.Invoice)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(actions => {
                const byJobId: { [jobId: number]: JobAction[] } = {};
                actions.forEach(a => {
                    (byJobId[a.jobId] = byJobId[a.jobId] || []).push(a);
                });
                this.actionsData = byJobId;
            });
    }

    // The first (per the backend's sort order) Invoice-type action for a job
    // sits on the finance row itself; any others render as extra rows
    // underneath it.
    getExtraActions(jobId: number): JobAction[] {
        return this.actionsData[jobId]?.slice(1) || [];
    }

    getStatus(action: JobAction): string {
        if (!action) {
            return '--';
        }

        if (action.actionStatus === ActionStatus.Waiting) {
            const name = this.getWaitingUserName(action) ?? this.getWaitingGroupName(action);
            return name ? `Waiting ${name}` : 'Waiting';
        }

        return getStatusByValue(action.actionStatus)?.title ?? '--';
    }

    isWaitingForPerson(action: JobAction): boolean {
        return !!action && action.actionStatus === ActionStatus.Waiting && !!action.waitingUserId;
    }

    isWaitingForGroup(action: JobAction): boolean {
        return !!action && action.actionStatus === ActionStatus.Waiting
            && !action.waitingUserId
            && !!this.getWaitingGroupName(action);
    }

    getWaitingUserName(action: JobAction): string | undefined {
        return action.waitingUser?.shortName;
    }

    getWaitingGroupName(action: JobAction): string | undefined {
        return action.waitingGroup?.groupName;
    }

    shouldHighlightRow(date: string, warning: boolean = false): boolean {
        return date ? shouldHighlightDate(date, warning) : false;
    }

    // Copied from action-table.component.ts's openDatepicker - opens the
    // hidden mat-datepicker behind a Scheduled/Deadline cell, starting on the
    // cell's existing date (if in the future) or today.
    openDatepicker(picker: any, event?: MouseEvent, cellDate?: Date | string): void {
        event?.stopPropagation();

        const dp: any = picker?.matDatepickerElement;
        if (!dp) return;

        const today = this.dateAdapter.today();

        if (cellDate) {
            const dateValue = new Date(cellDate);
            if (!isNaN(dateValue.getTime()) && dateValue > today) {
                dp.startAt = dateValue;
            } else {
                dp.startAt = today;
            }
        } else {
            dp.startAt = today;
        }

        dp.open();
    }

    onActionPropChange({ value }, action: JobAction, prop: string): void {
        if (!action) {
            return;
        }

        const updatedAction = <JobAction>{ ...action };
        updatedAction[prop] = value;
        this.actionUpdate.emit(updatedAction);
    }

    onClearDate(action: JobAction, prop: string): void {
        this.onActionPropChange({ value: null }, action, prop);
    }

    onSelectAction(action: JobAction, propertyKey: string): void {
        if (!action) {
            return;
        }

        this.actionSelected.emit(<JobActionUpdate>{ action, propertyKey });
    }

    // Used as a trackBy for the extra-action sub-rows *ngFor. getExtraActions()
    // returns a fresh array on every change-detection pass (it's a live slice
    // of actionsData, not a cached property), so without a trackBy Angular's
    // default identity comparison would tear down and rebuild every sub-row's
    // DOM - including any open <date-picker> - on every tick. Tracking by
    // jobActionId keeps a stable action's row (and its picker) in place.
    trackByActionId(index: number, action: JobAction): number {
        return action.jobActionId;
    }

    // Used as a trackBy for the outer per-finance *ngFor. Any inline
    // Scheduled/Deadline/Status edit on the Month End table triggers a full
    // finances reload (via _search$.next in finance-listing.component.ts), so
    // without a trackBy every row's DOM - including other rows' open
    // datepickers or menus - would be torn down and rebuilt after every
    // single edit. version.id is the same identity already used by
    // selectFinance()/financeLink() elsewhere in this component.
    trackByFinanceId(index: number, finance: Finance): number {
        return finance.version.id;
    }

    ngOnDestroy() {
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
    }


    isAllSelected(): boolean {
        return this.finances?.length > 0
            && this.selectedFinances.length === this.finances.length;
    }

    isSomeSelected(): boolean {
        return this.selectedFinances.length > 0
            && this.selectedFinances.length < this.finances.length;
    }


    isSelected(finance: Finance): boolean {
        return this.selectedFinances
            .some(f => f.version.id === finance.version.id);
    }

    onBulkSelect(finance: Finance, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        if (checked) {
            this.selectedFinances = [...this.selectedFinances, finance];
        } else {
            this.selectedFinances = this.selectedFinances
                .filter(f => f.version.id !== finance.version.id);
        }
        this.selectionChanged.emit(this.selectedFinances);
    }

    onBulkSelectAll(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.selectedFinances = checked ? [...this.finances] : [];
        this.selectionChanged.emit(this.selectedFinances);
    }

    public get bulkPdfEnabled(): boolean {
        return this.selectedFinances.some(f =>
            f.version.hasApprovals ||
            (f.financeType === 3 && f.version.financeStatusCode !== 'INTDFT')
        );
    }

    public get bulkColumnEnabled(): boolean {
        return this.finances.some(f =>
            f.version.hasApprovals ||
            (f.financeType === 3 && f.version.financeStatusCode !== 'INTDFT') ||
            this.canBulkApprove(f) ||
            this.canBulkSendForApproval(f)
        );
    }

    public get bulkOpenEnabled(): boolean {
        return this.selectedFinances.length > 0;
    }

    async onBulkOpenInNewTabs(): Promise<void> {
        const BATCH_SIZE = 5;  // how many to open at once
        const PAUSE_MS = 200; // how long to wait between batches

        const openOne = (url: string) => {
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        };

        const buildUrl = (id: number) => {
            const tree = this._router.createUrlTree([
                '/finance',
                convertFinanceTypeToName(this.financeType, true),
                id
            ]);
            return this._router.serializeUrl(tree);
        };

        // Open in batches with a pause to avoid overwhelming the browser
        for (let i = 0; i < this.selectedFinances.length; i += BATCH_SIZE) {
            const batch = this.selectedFinances.slice(i, i + BATCH_SIZE);
            batch.forEach(f => openOne(buildUrl(f.version.id)));
            await new Promise(r => setTimeout(r, PAUSE_MS));
        }

        setTimeout(() => window.focus(), 0);
        this.bulkMenuTrigger.closeMenu();
    }

    onBulkViewPdfs(): void {
        const viewables = this.selectedFinances.filter(f =>
            f.version.hasApprovals ||
            (f.financeType === 3 && f.version.financeStatusCode !== 'INTDFT')
        );
        viewables.forEach(finance => {
            const url = `/finance/downloads/${finance.version.id}/view`;
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        });
    }

    onBulkDownloadPdfs(): void {
        const downloadables = this.selectedFinances.filter(f =>
            f.version.hasApprovals ||
            (f.financeType === 3 && f.version.financeStatusCode !== 'INTDFT')
        );
        downloadables.forEach(finance => {
            const url = `/finance/downloads/${finance.version.id}/download`;
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        });
    }

    private canBulkConfirmByClient(finance: Finance): boolean {
        return this._loginService.checkAuth(['finance']) &&
            finance.nextStatus?.financeStatusCode === 'CLTAPL';
    }


    public get bulkConfirmByClientsEnabled(): boolean {
        return this.selectedFinances.some(f => this.canBulkConfirmByClient(f));
    }

    // Mirrors the per-row "Approve" step (onNextStatusStep's requiresApproval
    // branch), minus the approve/reject modal - bulk approval always
    // approves, same as the single-row action does when the user isn't
    // rejecting. Restricted to finance/admin only - unlike the single-row
    // approval (which also allows managers), bulk approval is limited to
    // finance/admin, so this deliberately doesn't reuse that wider gate.
    canBulkApprove(finance: Finance): boolean {
        return this.canActionNextStep(finance) &&
            !!finance.nextStatus?.requiresApproval &&
            this._loginService.checkAuth(['finance', 'admin']);
    }

    public get bulkApproveEnabled(): boolean {
        return this.selectedFinances.some(f => this.canBulkApprove(f));
    }

    // Mirrors the per-row "Send for approval" step (Draft -> Sent for
    // approval, financeStatusCode INTAPL), which doesn't itself require
    // approval, so onNextStatusStep's else-branch just calls
    // createVersionStatus directly with no modal. Scoped to the exact
    // INTAPL code (the same idiom canBulkConfirmByClient already uses for
    // CLTAPL) so it can never accidentally match some other
    // non-approval-required transition - restricted to finance/admin only.
    canBulkSendForApproval(finance: Finance): boolean {
        return this.canActionNextStep(finance) &&
            finance.nextStatus?.financeStatusCode === 'INTAPL' &&
            this._loginService.checkAuth(['finance', 'admin']);
    }

    public get bulkSendForApprovalEnabled(): boolean {
        return this.selectedFinances.some(f => this.canBulkSendForApproval(f));
    }

    async onBulkSendForApproval(): Promise<void> {
        this.bulkMenuTrigger.closeMenu();

        const toSubmit = this.selectedFinances.filter(f => this.canBulkSendForApproval(f));

        for (const f of toSubmit) {
            try {
                await lastValueFrom(
                    this._financeService.createVersionStatus(
                        f.financeId!,
                        f.version.id,
                        f.nextStatus!.financeStatusCode!
                    )
                );
            } catch (err: any) {
                if (![2601, 2627].includes(err?.error?.number)) throw err;
            }
        }

        // Refresh the finances list
        await lastValueFrom(
            this._financeService.getFinances(this._currentFilterQuery)
        );

        this.cd.detectChanges();
    }

    async onBulkApprove(): Promise<void> {
        this.bulkMenuTrigger.closeMenu();

        const toApprove = this.selectedFinances.filter(f => this.canBulkApprove(f));

        for (const f of toApprove) {
            try {
                await lastValueFrom(
                    this._financeService.createVersionStatus(
                        f.financeId!,
                        f.version.id,
                        f.nextStatus!.financeStatusCode!
                    )
                );
            } catch (err: any) {
                if (![2601, 2627].includes(err?.error?.number)) throw err;
            }
        }

        // Refresh the finances list
        await lastValueFrom(
            this._financeService.getFinances(this._currentFilterQuery)
        );

        this.cd.detectChanges();
    }

    async onBulkConfirmByClients(): Promise<void> {
        this.bulkMenuTrigger.closeMenu();

        const toConfirm = this.selectedFinances.filter(f =>
            this.canBulkConfirmByClient(f)
        );

        for (const f of toConfirm) {
            try {
              await lastValueFrom(
                  this._financeService.createVersionStatus(
                      f.financeId!,
                      f.version.id,
                      f.nextStatus!.financeStatusCode!
                  )
              );
            } catch (err: any) {
                if (![2601, 2627].includes(err?.error?.number)) throw err;
            }
        }

        // Refresh the finances list
        await lastValueFrom(
            this._financeService.getFinances(this._currentFilterQuery)
        );

        this.cd.detectChanges();
    }

    public financeLink(fin: Finance) {
        return ['/finance', convertFinanceTypeToName(this.financeType, true), fin.version.id];
    }
}
